import { useEffect, useState } from "react";
import { ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Sparkle, TrendUp, TrendDown, CurrencyInr, UsersThree, CursorClick, Fire, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AIDrawer, { useAIStream } from "@/components/AIDrawer";
import { SetupChecklist } from "@/components/SetupChecklist";
import { AIImpactReport } from "@/components/AIImpactReport";
import { api, inr, compact } from "@/lib/api";

const PERIODS = [
  { v: "last_14d", l: "Last 14 days" },
  { v: "today", l: "Today" },
  { v: "yesterday", l: "Yesterday" },
  { v: "this_week", l: "This week" },
  { v: "prev_week", l: "Previous week" },
  { v: "last_7d", l: "Last 7 days" },
  { v: "last_30d", l: "Last 30 days" },
  { v: "this_month", l: "This month" },
  { v: "last_month", l: "Last month" },
  { v: "this_quarter", l: "This quarter" },
  { v: "prev_quarter", l: "Previous quarter" },
  { v: "custom", l: "Custom range…" },
];

const StatCard = ({ icon: Icon, label, value, delta, sub, testId }) => (
  <div data-testid={testId} className="bg-white border border-[#E5E7EB] rounded-lg p-6 hover:-translate-y-1 hover:border-[#c9cdd6] transition-transform duration-200">
    <div className="flex items-center justify-between">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8a90a0]">{label}</span>
      <Icon size={18} weight="duotone" className="text-[#0052FF]" />
    </div>
    <div className="mt-3 font-heading text-3xl font-extrabold tracking-tighter text-[#0A0D14]">{value}</div>
    <div className="mt-1.5 flex items-center gap-2 text-xs">
      {delta !== undefined && (
        <span className={`inline-flex items-center gap-1 font-bold ${delta >= 0 ? "text-[#00953e]" : "text-red-500"}`}>
          {delta >= 0 ? <TrendUp size={13} weight="bold" /> : <TrendDown size={13} weight="bold" />}
          {Math.abs(delta)}%
        </span>
      )}
      <span className="text-[#8a90a0]">{sub}</span>
    </div>
  </div>
);

export default function Overview() {
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [preset, setPreset] = useState("last_14d");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const [loading, setLoading] = useState(false);
  const ai = useAIStream();

  useEffect(() => {
    if (preset === "custom" && (!dFrom || !dTo)) return;
    setLoading(true);
    const params = preset === "custom" ? `?preset=custom&date_from=${dFrom}&date_to=${dTo}` : `?preset=${preset}`;
    api.get(`/dashboard/overview${params}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [preset, dFrom, dTo]);

  const runPortfolio = () => {
    setDrawer({ title: "Portfolio Decisions", subtitle: "Full account analysis · all campaigns & audiences" });
    ai.start({ type: "portfolio" });
  };

  if (!data)
    return <div className="grid grid-cols-1 md:grid-cols-4 gap-6">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-36 rounded-lg" />)}</div>;

  const { totals, timeseries, accounts, top_campaigns } = data;
  const live = data.range?.live;
  const deltaSub = live ? "vs previous period" : "vs prev 7d";
  const rangeLabel = live
    ? `${data.range.since} → ${data.range.until}`
    : data.range?.last_sync
      ? `last 14 days · data as of ${new Date(data.range.last_sync).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} (auto-syncs every 3h)`
      : "last 14 days (synced)";

  return (
    <div className="space-y-6">
      <SetupChecklist />
      <div className="flex flex-wrap items-center gap-2" data-testid="overview-period-bar">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#525866]">
          <CalendarBlank size={15} weight="duotone" className="text-[#0052FF]" /> Period
        </span>
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="w-44 h-9 text-xs font-semibold bg-white" data-testid="overview-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
        </Select>
        {preset === "custom" && (
          <div className="flex items-center gap-1.5" data-testid="overview-custom-range">
            <input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} data-testid="overview-date-from"
              className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
            <span className="text-[11px] text-[#8a90a0] font-bold">to</span>
            <input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} data-testid="overview-date-to"
              className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
          </div>
        )}
        <span className="text-[11px] text-[#8a90a0] font-mono ml-1" data-testid="overview-range-label">
          {loading ? "Fetching live insights from Meta…" : rangeLabel}
        </span>
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-opacity ${loading ? "opacity-50" : ""}`}>
        <StatCard icon={CurrencyInr} label="Total Spend" value={inr(totals.spend)} delta={totals.spend_delta} sub={deltaSub} testId="stat-spend" />
        <StatCard icon={UsersThree} label="Leads" value={totals.leads} delta={totals.leads_delta} sub={deltaSub} testId="stat-leads" />
        <StatCard icon={CursorClick} label="Avg CPL" value={inr(totals.cpl)} sub={`CTR ${totals.ctr}% · ${compact(totals.impressions)} impressions`} testId="stat-cpl" />
        <StatCard icon={Fire} label="Hot Leads" value={totals.hot_leads} sub={`Avg quality score ${totals.avg_quality}/100`} testId="stat-hot-leads" />
      </div>

      <AIImpactReport />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">Spend vs Leads</h2>
              <p className="text-xs text-[#8a90a0] mt-0.5">Daily performance across all {accounts.length} ad accounts</p>
            </div>
            <div className="flex gap-4 text-xs font-semibold">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#0052FF]" />Spend</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-ai" />Leads</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={timeseries}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0052FF" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#0052FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a90a0" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#8a90a0" }} tickFormatter={(v) => `₹${compact(v)}`} axisLine={false} tickLine={false} width={54} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#8a90a0" }} axisLine={false} tickLine={false} width={30} />
              <Tooltip formatter={(v, n) => (n === "spend" ? [inr(v), "Spend"] : [v, "Leads"])} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Area yAxisId="l" type="monotone" dataKey="spend" stroke="#0052FF" strokeWidth={2} fill="url(#spendGrad)" />
              <Bar yAxisId="r" dataKey="leads" fill="#6366F1" radius={[3, 3, 0, 0]} barSize={14} opacity={0.85} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="ai-panel rounded-lg p-6 flex flex-col" data-testid="ai-decision-center">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-ai/10 flex items-center justify-center"><Sparkle size={18} weight="duotone" className="text-ai" /></span>
            <div>
              <h2 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">AI Decision Center</h2>
              <p className="text-[11px] text-[#8a90a0]">Claude Opus 4.7 strategist</p>
            </div>
          </div>
          <p className="text-sm text-[#525866] leading-relaxed mt-4 flex-1">
            Analyze all {accounts.length} accounts, {top_campaigns.length}+ campaigns and lead quality data. Get scale / pause / budget-shift decisions in seconds.
          </p>
          <div className="space-y-2 mt-4">
            {top_campaigns.slice(0, 3).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-xs bg-white/70 rounded-md border border-[#E5E7EB] px-3 py-2">
                <span className="font-semibold text-[#0A0D14] truncate mr-2">{c.name}</span>
                <span className="font-mono text-[#525866] shrink-0">{c.leads} leads · {inr(c.cpl)} CPL</span>
              </div>
            ))}
          </div>
          <Button onClick={runPortfolio} data-testid="run-portfolio-analysis-button"
            className="mt-5 w-full rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold transition-transform hover:-translate-y-[1px] active:scale-[0.98]">
            <Sparkle size={16} weight="fill" className="mr-1.5" /> Run Portfolio Analysis
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {accounts.map((a) => (
          <div key={a.id} data-testid={`account-card-${a.id}`} className="bg-white border border-[#E5E7EB] rounded-lg p-6 hover:-translate-y-1 hover:border-[#c9cdd6] transition-transform duration-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="font-heading text-sm font-extrabold tracking-tight text-[#0A0D14] truncate">{a.name}</h3>
                <p className="text-[11px] font-mono text-[#8a90a0] mt-0.5">{a.id}</p>
              </div>
              <Badge className="bg-success/10 text-[#00953e] border border-success/25 rounded-full font-bold text-[10px] shrink-0">ACTIVE</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div><div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Spend</div><div className="font-heading font-extrabold text-[#0A0D14] mt-1">{inr(a.spend)}</div></div>
              <div><div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Leads</div><div className="font-heading font-extrabold text-[#0A0D14] mt-1">{a.leads}</div></div>
              <div><div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">CPL</div><div className="font-heading font-extrabold text-[#0A0D14] mt-1">{a.cpl ? inr(a.cpl) : "—"}</div></div>
            </div>
            <div className="mt-4 text-xs text-[#8a90a0]">{a.active_campaigns}/{a.campaigns} campaigns active</div>
          </div>
        ))}
      </div>

      <AIDrawer open={!!drawer} onClose={() => { setDrawer(null); ai.reset(); }} title={drawer?.title} subtitle={drawer?.subtitle} text={ai.text} running={ai.running} error={ai.error} />
    </div>
  );
}
